import {useState, useMemo} from 'react';
import {useParams, Link} from 'react-router-dom';
import {trpc} from '@common-web/lib/trpc';
import {MS_PER_DAY} from '@common/constants/time';
import {ArrowLeft, Calendar, Clock, Target, TrendingUp} from 'lucide-react';
import {PageHeader, PageHeaderAction} from '@common-web/components/page-header';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Badge} from '@common-web/components/ui/badge';
import {OnboardingChecklist} from './components/OnboardingChecklist';
import {OnboardingTimeline} from './components/OnboardingTimeline';
import {StepDetailSheet} from './components/StepDetailSheet';
import {RiskIndicator} from './components/RiskIndicator';
import {StageProgressBar} from './components/StageProgressBar';
import {AssigneeAvatar} from './components/AssigneeAvatar';
import {DealInformationPanel} from './components/DealInformationPanel';

type RiskLevel = 'on_track' | 'at_risk' | 'stalled';

interface OnboardingStep {
  id: string;
  stepKey: string;
  label: string;
  phaseGroup: string;
  order: number;
  isRequired: boolean;
  status: string;
  slaTargetDays: number;
  assignee: {id: string; name: string} | null;
  completedBy: {id: string; name: string} | null;
  completedAt: Date | null;
  startedAt: Date | null;
  notes: string | null;
}

function computeRiskLevel(steps: OnboardingStep[], onboardingStartedAt: Date): RiskLevel {
  const now = new Date();
  let hasAtRisk = false;

  for (const step of steps) {
    if (step.status === 'COMPLETED' || step.status === 'SKIPPED') continue;
    if (step.status !== 'NOT_STARTED' && step.status !== 'IN_PROGRESS') continue;

    const stepStart = step.startedAt ? new Date(step.startedAt) : new Date(onboardingStartedAt);
    const daysSinceStart = Math.floor(
      (now.getTime() - stepStart.getTime()) / MS_PER_DAY,
    );

    if (daysSinceStart > step.slaTargetDays) {
      hasAtRisk = true;
    }
  }

  // Check for stalled: no activity in 7 days on any active step
  const activeSteps = steps.filter((s) => s.status === 'IN_PROGRESS' || s.status === 'NOT_STARTED');
  if (activeSteps.length > 0) {
    const mostRecentActivity = activeSteps.reduce<Date | null>((latest, step) => {
      const stepDate = step.startedAt ? new Date(step.startedAt) : null;
      if (!stepDate) return latest;
      if (!latest) return stepDate;
      return stepDate > latest ? stepDate : latest;
    }, null);

    if (mostRecentActivity) {
      const daysSinceLastUpdate = Math.floor(
        (now.getTime() - mostRecentActivity.getTime()) / MS_PER_DAY,
      );
      if (daysSinceLastUpdate > 7) {
        return 'stalled';
      }
    }
  }

  return hasAtRisk ? 'at_risk' : 'on_track';
}

function formatDate(date: Date | string | null): string {
  if (!date) return 'Not set';
  const d = new Date(date);
  return d.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'});
}

function daysBetween(from: Date | string, to: Date | string): number {
  const start = new Date(from);
  const end = new Date(to);
  return Math.floor((end.getTime() - start.getTime()) / MS_PER_DAY);
}

const statusBadgeClasses: Record<string, string> = {
  ACTIVE: 'bg-emerald-500/10 text-emerald-700 border border-emerald-500/20',
  COMPLETED: 'bg-blue-500/10 text-blue-700 border border-blue-500/20',
  PAUSED: 'bg-amber-500/10 text-amber-700 border border-amber-500/20',
  CANCELLED: 'bg-red-500/10 text-red-700 border border-red-500/20',
};

const phaseBadgeClasses: Record<string, string> = {
  INITIAL_SETUP: 'bg-blue-900/10 text-blue-900 border border-blue-900/20',
  PRE_ENROLLMENT: 'bg-blue-600/10 text-blue-600 border border-blue-600/20',
  ENROLLMENT: 'bg-blue-500/10 text-blue-500 border border-blue-500/20',
  GO_LIVE_PREP: 'bg-amber-600/10 text-amber-600 border border-amber-600/20',
  POST_LAUNCH: 'bg-emerald-600/10 text-emerald-600 border border-emerald-600/20',
};

function formatPhaseName(phase: string): string {
  return phase
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

export function OnboardingGroupDetail() {
  const {onboardingId} = useParams<{onboardingId: string}>();
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'checklist' | 'deal'>('checklist');

  const {data: onboarding} = trpc.internal.onboarding.getOnboarding.useQuery(
    {onboardingId: onboardingId!},
    {enabled: !!onboardingId},
  );

  const {data: activityData} = trpc.internal.onboarding.getActivity.useQuery(
    {onboardingId: onboardingId!, page: 1, pageSize: 50},
    {enabled: !!onboardingId},
  );

  const riskLevel = useMemo<RiskLevel>(() => {
    if (!onboarding) return 'on_track';
    return computeRiskLevel(onboarding.steps, onboarding.startedAt);
  }, [onboarding]);

  const completedSteps = useMemo(() => {
    if (!onboarding) return 0;
    return onboarding.steps.filter(
      (s: {status: string}) => s.status === 'COMPLETED' || s.status === 'SKIPPED',
    ).length;
  }, [onboarding]);

  const totalSteps = onboarding?.steps.length ?? 0;
  const progressPercent = totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;
  const daysInPipeline = onboarding ? daysBetween(onboarding.startedAt, new Date()) : 0;

  function handleStepClick(stepId: string) {
    setSelectedStepId(stepId);
    setSheetOpen(true);
  }

  if (!onboardingId) {
    return (
      <div className='flex items-center justify-center min-h-[400px]'>
        <p className='text-gray-500'>Onboarding not found.</p>
      </div>
    );
  }

  if (!onboarding) {
    return (
      <div className='flex items-center justify-center min-h-[400px]'>
        <p className='text-gray-500'>Loading...</p>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-blue/5'>
      <div className='flex flex-col gap-0'>
        <PageHeader title={onboarding.groupName}>
          <PageHeaderAction tone='secondary' asChild>
            <Link to='/onboarding'>
              <ArrowLeft className='h-4 w-4 mr-1' />
              Pipeline
            </Link>
          </PageHeaderAction>
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusBadgeClasses[onboarding.status] ?? 'bg-muted text-muted-foreground border'}`}
          >
            {onboarding.status}
          </span>
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${phaseBadgeClasses[onboarding.currentPhase] ?? 'bg-muted text-muted-foreground border'}`}
          >
            {formatPhaseName(onboarding.currentPhase)}
          </span>
          <RiskIndicator level={riskLevel} />
          {onboarding.assignedManager && (
            <div className='flex items-center gap-2'>
              <AssigneeAvatar name={onboarding.assignedManager.name} size='sm' />
              <span className='text-sm text-muted-foreground'>
                {onboarding.assignedManager.name}
              </span>
            </div>
          )}
        </PageHeader>

        {/* Main content */}
        <div className='px-8 py-6 max-w-7xl mx-auto w-full space-y-6'>
          {/* 4 overview metric cards */}
          <div className='grid grid-cols-4 gap-4'>
            {/* Days in Pipeline */}
            <Card className='border-gray-200 shadow-sm hover:shadow-md transition-shadow'>
              <CardContent className='p-4'>
                <div className='flex items-start justify-between'>
                  <div>
                    <p className='text-xs text-brand-gray'>Days in Pipeline</p>
                    <p className='text-2xl font-bold text-brand-navy mt-1'>{daysInPipeline}</p>
                  </div>
                  <Clock className='h-5 w-5 text-brand-blue' />
                </div>
              </CardContent>
            </Card>

            {/* Progress */}
            <Card className='border-gray-200 shadow-sm hover:shadow-md transition-shadow'>
              <CardContent className='p-4'>
                <div className='flex items-start justify-between'>
                  <div>
                    <p className='text-xs text-brand-gray'>Progress</p>
                    <p className='text-2xl font-bold text-emerald-600 mt-1'>{progressPercent}%</p>
                    <p className='text-xs text-brand-gray-light mt-0.5'>
                      {completedSteps}/{totalSteps} steps
                    </p>
                  </div>
                  <TrendingUp className='h-5 w-5 text-emerald-600' />
                </div>
                <div className='mt-2'>
                  <StageProgressBar steps={onboarding.steps} />
                </div>
              </CardContent>
            </Card>

            {/* Risk Score */}
            <Card className='border-gray-200 shadow-sm hover:shadow-md transition-shadow'>
              <CardContent className='p-4'>
                <div className='flex items-start justify-between'>
                  <div>
                    <p className='text-xs text-brand-gray'>Risk Score</p>
                    <div className='mt-1'>
                      <RiskIndicator level={riskLevel} />
                    </div>
                  </div>
                  <Target className='h-5 w-5 text-brand-gray' />
                </div>
              </CardContent>
            </Card>

            {/* Target Completion */}
            <Card className='border-gray-200 shadow-sm hover:shadow-md transition-shadow'>
              <CardContent className='p-4'>
                <div className='flex items-start justify-between'>
                  <div>
                    <p className='text-xs text-brand-gray'>Target Completion</p>
                    <p className='text-2xl font-bold text-brand-navy mt-1'>
                      {formatDate(onboarding.targetCompletionDate)}
                    </p>
                  </div>
                  <Calendar className='h-5 w-5 text-brand-blue' />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Two-column layout */}
          <div className='grid grid-cols-3 gap-6'>
            {/* Left column */}
            <div className='col-span-2 space-y-0'>
              {/* Tab bar */}
              <div className='flex border-b mb-0'>
                <button
                  type='button'
                  onClick={() => setActiveTab('checklist')}
                  className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'checklist'
                      ? 'border-brand-blue text-brand-blue'
                      : 'border-transparent text-brand-gray hover:text-brand-navy'
                  }`}
                >
                  Onboarding Checklist
                </button>
                <button
                  type='button'
                  onClick={() => setActiveTab('deal')}
                  className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'deal'
                      ? 'border-brand-blue text-brand-blue'
                      : 'border-transparent text-brand-gray hover:text-brand-navy'
                  }`}
                >
                  Deal Information
                  {onboarding.rfcSubmission && (
                    <span className='ml-1.5 inline-flex items-center rounded-full bg-brand-blue/10 px-1.5 py-0.5 text-xs text-brand-blue'>
                      Linked
                    </span>
                  )}
                </button>
              </div>

              {activeTab === 'checklist' ? (
                <Card className='rounded-t-none border-t-0'>
                  <CardContent className='pt-4'>
                    <OnboardingChecklist steps={onboarding.steps} onStepClick={handleStepClick} />
                  </CardContent>
                </Card>
              ) : (
                <Card className='rounded-t-none border-t-0'>
                  <CardContent className='pt-4'>
                    {onboarding.rfcSubmission ? (
                      <>
                        <div className='flex items-center justify-between mb-4'>
                          <p className='text-sm text-brand-gray'>
                            Deal data from{' '}
                            <Link
                              to={`/rfc/${onboarding.rfcSubmission.id}`}
                              className='text-brand-blue hover:underline font-medium'
                            >
                              {onboarding.rfcSubmission.companyName ?? 'RFC Submission'}
                            </Link>
                          </p>
                        </div>
                        <DealInformationPanel rfcSubmission={onboarding.rfcSubmission} />
                      </>
                    ) : (
                      <div className='text-center py-12 text-brand-gray-light'>
                        <p className='text-sm'>No deal linked to this onboarding.</p>
                        <p className='text-xs mt-1'>
                          This onboarding was created without an RFC submission.
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Right column */}
            <div className='col-span-1 space-y-6'>
              <Card className='border-gray-200 shadow-sm'>
                <CardHeader>
                  <CardTitle className='text-lg font-semibold text-brand-navy'>Activity</CardTitle>
                </CardHeader>
                <CardContent>
                  <OnboardingTimeline activities={activityData?.activities ?? []} />
                </CardContent>
              </Card>

              <Card className='border-gray-200 shadow-sm'>
                <CardHeader>
                  <CardTitle className='text-lg font-semibold text-brand-navy'>
                    Group Info
                  </CardTitle>
                </CardHeader>
                <CardContent className='space-y-3'>
                  <div>
                    <p className='text-xs text-brand-gray'>Group</p>
                    <p className='text-sm font-medium text-brand-navy'>
                      {onboarding.group?.name ?? onboarding.groupName}
                    </p>
                  </div>
                  <div>
                    <p className='text-xs text-brand-gray'>Template</p>
                    <p className='text-sm font-medium text-brand-navy'>
                      {onboarding.template.name}
                    </p>
                  </div>
                  <div>
                    <p className='text-xs text-brand-gray'>Started</p>
                    <p className='text-sm font-medium text-brand-navy'>
                      {formatDate(onboarding.startedAt)}
                    </p>
                  </div>
                  <div>
                    <p className='text-xs text-brand-gray'>Manager</p>
                    <p className='text-sm font-medium text-brand-navy'>
                      {onboarding.assignedManager?.name ?? 'Unassigned'}
                    </p>
                  </div>
                  {onboarding.rfcSubmission && (
                    <div>
                      <p className='text-xs text-brand-gray'>Deal</p>
                      <Link
                        to={`/rfc/${onboarding.rfcSubmission.id}`}
                        className='text-sm font-medium text-brand-blue hover:underline'
                      >
                        {onboarding.rfcSubmission.companyName ?? onboarding.rfcSubmission.id}
                      </Link>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Step Detail Sheet */}
      <StepDetailSheet
        stepId={selectedStepId}
        onboardingId={onboardingId}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
      />
    </div>
  );
}
